/**
 * CDC/WHO growth-chart percentile engine via the LMS method — `BACKLOG.md` M8, `emr-po`'s
 * `docs/research/specialty-module-requirements.md`'s confirmed CDC/AAP policy: WHO growth standards
 * for birth to under 24 months, the CDC 2000 growth charts for 2 through 20 years.
 *
 * **The LMS math below is well-established, publicly documented statistics, not something this
 * module needs a primary-source citation for** (Cole T.J., "The LMS method for constructing
 * normalized growth standards", *European Journal of Clinical Nutrition*, 1990; both WHO's and
 * CDC's own growth-chart technical documentation state the identical formula). Given a value `X` and
 * the age/sex-specific parameters `L` (Box-Cox power), `M` (median), `S` (coefficient of variation):
 *
 *   Z = ((X / M)^L - 1) / (L * S)   when L ≠ 0
 *   Z = ln(X / M) / S               when L = 0
 *
 * and the percentile is the standard normal CDF of `Z`. `zScoreFromLms`/`percentileFromZScore` below
 * implement exactly this, independent of which reference table supplies `L`/`M`/`S` — the engine is
 * correct regardless of the review status of the table it is fed.
 *
 * **`REFERENCE_TABLE` is now entirely real, verified data, across all four measurement types** — see
 * `cdcGrowthReferenceData.ts`'s own header (2 through 20 years) and `whoGrowthReferenceData.ts`'s
 * own header (birth through 24 months) for the full source/verification/sampling account of each.
 * Both are monthly LMS values (matching each source's own true official publication granularity),
 * handed off at exactly the same day (731 = 24 months) with no gap or overlap.
 *
 * **BMI-for-age, added 2026-08-24** (`BACKLOG.md` M8's own scoped decision): `bmi` is wired in like
 * every other measurement type here, but it is never independently entered — `isGrowthMeasurementType`
 * accepts it (a stored row's `measurementType` really can be `'bmi'`, and `computeGrowthPercentile`
 * must compute a real percentile for it), while `CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES` below is
 * the narrower set the create-measurement request schema actually validates against, deliberately
 * excluding `'bmi'`. The value itself is server-computed by `computeBmi` from a same-encounter
 * `weight_kg` + `length_height_cm` pair and inserted as its own row —
 * `server/src/pediatrics/growthService.ts`'s `createGrowthMeasurement` is where that happens — never
 * typed directly, since a typed BMI could disagree with the recorded weight/height it should be
 * derived from.
 *
 * Coverage is still deliberately narrow in two ways, and each gap is a documented limitation, not a
 * silent one:
 *   - `head_circumference_cm` only has real coverage from birth through 36 months — WHO from birth
 *     through 24 months, CDC from 24 through 36 months (CDC does not publish this chart further;
 *     see `cdcGrowthReferenceData.ts`'s own header). `lmsForAge` returns `null` past 36 months, and
 *     `computeGrowthPercentile` reports `age_out_of_range` there rather than guessing.
 *   - No 2–5-year WHO/CDC gradual-transition blending (a real, published refinement some growth-chart
 *     tools implement) — this module does a hard cutover at 24 months, WHO before, CDC at and after.
 *
 * This pass deliberately stays at plain percentile display for BMI — no CDC/AAP classification-band
 * flagging (underweight/healthy/overweight/obesity). That would be a second real DSI matching C12's
 * non-directive/source-attribute-disclosure treatment, out of scope here per `BACKLOG.md` M8's own
 * decision, left for a dedicated pass.
 */

import {
  CDC_BMI_REFERENCE,
  CDC_HEAD_CIRCUMFERENCE_CM_REFERENCE,
  CDC_LENGTH_HEIGHT_CM_REFERENCE,
  CDC_WEIGHT_KG_REFERENCE,
} from './cdcGrowthReferenceData.ts';
import {
  WHO_BMI_REFERENCE,
  WHO_HEAD_CIRCUMFERENCE_CM_REFERENCE,
  WHO_LENGTH_CM_REFERENCE,
  WHO_WEIGHT_KG_REFERENCE,
} from './whoGrowthReferenceData.ts';

export const GROWTH_MEASUREMENT_TYPES = [
  'weight_kg',
  'length_height_cm',
  'head_circumference_cm',
  'bmi',
] as const;
export type GrowthMeasurementType = (typeof GROWTH_MEASUREMENT_TYPES)[number];

export function isGrowthMeasurementType(value: string): value is GrowthMeasurementType {
  return (GROWTH_MEASUREMENT_TYPES as readonly string[]).includes(value);
}

/**
 * The subset of `GROWTH_MEASUREMENT_TYPES` a clinician can directly enter through the
 * create-growth-measurement route (`shared/src/pediatricsRequests.ts`'s
 * `createGrowthMeasurementRequestSchema`) and the web entry form
 * (`web/src/routes/PatientPediatricsPage.tsx`'s `GrowthSection`). `'bmi'` is deliberately excluded —
 * it is always server-computed from a same-encounter `weight_kg` + `length_height_cm` pair (see this
 * module's header and `server/src/pediatrics/growthService.ts`), never independently typed.
 */
export const CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES = [
  'weight_kg',
  'length_height_cm',
  'head_circumference_cm',
] as const satisfies readonly GrowthMeasurementType[];
export type ClientWritableGrowthMeasurementType =
  (typeof CLIENT_WRITABLE_GROWTH_MEASUREMENT_TYPES)[number];

/**
 * Growth charts are sex-specific by clinical design — WHO and CDC each publish separate "boys" and
 * "girls" tables, and using the recorded value to pick the matching table is the field's actual
 * intended use. **This is not the demographic-inference pattern `dermatology/patientAssessment.ts`'s
 * header forbids** — that guardrail is about deriving a *different* clinical fact (skin phototype,
 * pregnancy-risk category) from a demographic proxy; here, sex is the correct, standard parameter of
 * the chart itself, the same way it is the correct parameter of a pediatric reference range anywhere
 * else in medicine. `computeGrowthPercentile` still refuses outright for `'other'`/`'unknown'` rather
 * than guessing which published table to use — WHO/CDC do not publish one for either.
 */
export type GrowthChartSex = 'male' | 'female';

export function isGrowthChartSex(value: string): value is GrowthChartSex {
  return value === 'male' || value === 'female';
}

export interface LmsParameters {
  readonly l: number;
  readonly m: number;
  readonly s: number;
}

type ReferenceRow = LmsParameters & { readonly ageDays: number };

type ReferenceTable = Readonly<
  Record<GrowthMeasurementType, Readonly<Record<GrowthChartSex, readonly ReferenceRow[]>> | null>
>;

const DAYS_PER_MONTH = 30.4375; // 365.25 / 12 — the same average-month convention CDC's own age
// calculators use, since a calendar month has no fixed day count.
const WHO_CDC_CUTOVER_DAYS = 24 * DAYS_PER_MONTH; // 24 months

/** Every row is real data — WHO-range (birth–24mo) from `whoGrowthReferenceData.ts`, CDC-range
 * (24mo–20yr) from `cdcGrowthReferenceData.ts`. Both halves already end/start exactly at the same
 * day (730/731), so concatenation alone produces a gapless, non-overlapping combined table. */
export const REFERENCE_TABLE: ReferenceTable = {
  weight_kg: {
    male: [...WHO_WEIGHT_KG_REFERENCE.male, ...CDC_WEIGHT_KG_REFERENCE.male],
    female: [...WHO_WEIGHT_KG_REFERENCE.female, ...CDC_WEIGHT_KG_REFERENCE.female],
  },
  length_height_cm: {
    male: [...WHO_LENGTH_CM_REFERENCE.male, ...CDC_LENGTH_HEIGHT_CM_REFERENCE.male],
    female: [...WHO_LENGTH_CM_REFERENCE.female, ...CDC_LENGTH_HEIGHT_CM_REFERENCE.female],
  },
  // Real data, birth through 36 months only — WHO from birth through 24 months, CDC from 24 through
  // 36 months (CDC does not publish this chart past 36 months). See this module's header.
  head_circumference_cm: {
    male: [
      ...WHO_HEAD_CIRCUMFERENCE_CM_REFERENCE.male,
      ...CDC_HEAD_CIRCUMFERENCE_CM_REFERENCE.male,
    ],
    female: [
      ...WHO_HEAD_CIRCUMFERENCE_CM_REFERENCE.female,
      ...CDC_HEAD_CIRCUMFERENCE_CM_REFERENCE.female,
    ],
  },
  // WHO birth-24mo (recumbent-length-based) + CDC 24mo-20yr (height-based) -- same day-731 handoff
  // as every other measurement type here. See this module's header on why BMI is server-computed
  // only, never client-entered.
  bmi: {
    male: [...WHO_BMI_REFERENCE.male, ...CDC_BMI_REFERENCE.male],
    female: [...WHO_BMI_REFERENCE.female, ...CDC_BMI_REFERENCE.female],
  },
};

/** Age in whole days between two `YYYY-MM-DD` calendar dates. Negative if `onDate` precedes
 * `dateOfBirth` — callers treat that as an invalid/out-of-range age rather than this function
 * silently clamping it, matching `destructionCptCodes`'s own refuse-rather-than-guess convention. */
export function ageInDays(dateOfBirth: string, onDate: string): number {
  const dob = Date.parse(`${dateOfBirth}T00:00:00Z`);
  const on = Date.parse(`${onDate}T00:00:00Z`);
  return Math.round((on - dob) / 86_400_000);
}

/** WHO for birth through under 24 months; CDC from 24 months on. Exported so a caller (a route
 * response, a UI badge) can say which chart a given percentile actually came from. */
export function growthChartSourceForAgeDays(ageDays: number): 'who' | 'cdc' {
  return ageDays < WHO_CDC_CUTOVER_DAYS ? 'who' : 'cdc';
}

/** Linear interpolation between the two tabulated rows bracketing `ageDays` — see this module's
 * header on why this is coarser than the real published per-month/per-day tables. `null` outside the
 * table's own range (before birth, or past its last tabulated point). */
function interpolateLms(rows: readonly ReferenceRow[], ageDays: number): LmsParameters | null {
  const first = rows[0];
  const last = rows[rows.length - 1];
  if (first === undefined || last === undefined) return null;
  if (ageDays < first.ageDays || ageDays > last.ageDays) return null;

  for (let i = 0; i < rows.length - 1; i += 1) {
    const lo = rows[i];
    const hi = rows[i + 1];
    if (lo === undefined || hi === undefined) continue;
    if (ageDays >= lo.ageDays && ageDays <= hi.ageDays) {
      const span = hi.ageDays - lo.ageDays;
      const t = span === 0 ? 0 : (ageDays - lo.ageDays) / span;
      return {
        l: lo.l + (hi.l - lo.l) * t,
        m: lo.m + (hi.m - lo.m) * t,
        s: lo.s + (hi.s - lo.s) * t,
      };
    }
  }
  // Exact match on the final row.
  return { l: last.l, m: last.m, s: last.s };
}

/** The interpolated `L`/`M`/`S` for `measurementType`/`sex` at `ageDays`, or `null` if there is no
 * table for this measurement type at all, or `ageDays` falls outside the tabulated range. */
export function lmsForAge(
  measurementType: GrowthMeasurementType,
  sex: GrowthChartSex,
  ageDays: number,
): LmsParameters | null {
  const bySex = REFERENCE_TABLE[measurementType];
  if (bySex === null) return null;
  return interpolateLms(bySex[sex], ageDays);
}

/** The LMS-method Z-score for `value` given `lms` — Cole 1990, see this module's header. */
export function zScoreFromLms(value: number, lms: LmsParameters): number {
  if (lms.l === 0) return Math.log(value / lms.m) / lms.s;
  return (Math.pow(value / lms.m, lms.l) - 1) / (lms.l * lms.s);
}

/** Abramowitz & Stegun 7.1.26 — a standard, widely-cited rational approximation to the error
 * function, accurate to within 1.5e-7. Used only to turn a Z-score into a percentile for display; no
 * clinical decision in this codebase branches on the sixth decimal place. */
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  const ax = Math.abs(x);
  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;
  const t = 1 / (1 + p * ax);
  const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-ax * ax);
  return sign * y;
}

/** Standard normal CDF, as a 0–100 percentile. */
export function percentileFromZScore(z: number): number {
  const cdf = 0.5 * (1 + erf(z / Math.SQRT2));
  return cdf * 100;
}

/**
 * BMI = weight(kg) / height(m)^2 — the standard formula. `heightCm` is a `length_height_cm`
 * measurement, converted to meters before squaring; `weightKg` is a `weight_kg` measurement. Pure
 * and unvalidated by design — the caller (`server/src/pediatrics/growthService.ts`'s
 * `createGrowthMeasurement`) is responsible for only ever calling this with two real, already-stored,
 * same-encounter values, never client-supplied ones. See this module's header.
 */
export function computeBmi(weightKg: number, heightCm: number): number {
  const heightM = heightCm / 100;
  return weightKg / (heightM * heightM);
}

export interface GrowthPercentileInput {
  readonly measurementType: GrowthMeasurementType;
  readonly sex: string;
  readonly dateOfBirth: string;
  readonly measuredOn: string;
  readonly value: number;
}

export type GrowthPercentileResult =
  | {
      readonly ok: true;
      readonly ageDays: number;
      readonly source: 'who' | 'cdc';
      readonly zScore: number;
      readonly percentile: number;
    }
  | {
      readonly ok: false;
      readonly reason:
        'sex_not_supported' | 'no_reference_table' | 'age_out_of_range' | 'invalid_value';
    };

/**
 * The single entry point a route/service should call. Refuses rather than guesses in every failure
 * case, matching `destructionCptCodes`'s convention:
 *
 *   - `sex_not_supported` — WHO/CDC publish only male/female tables; `'other'`/`'unknown'` (and
 *     anything else) is refused outright rather than defaulted to either one.
 *   - `no_reference_table` — `measurementType` has no table at all. Currently unreachable: every
 *     declared `GrowthMeasurementType` has a real table in `REFERENCE_TABLE` today (see this
 *     module's own `growthCharts.test.ts` for the exhaustive proof this stays true), but the branch
 *     stays here for a future measurement type added without wiring its table.
 *   - `age_out_of_range` — before birth, or past the oldest tabulated age (20 years).
 *   - `invalid_value` — a non-finite or non-positive measurement.
 */
export function computeGrowthPercentile(input: GrowthPercentileInput): GrowthPercentileResult {
  if (!isGrowthChartSex(input.sex)) return { ok: false, reason: 'sex_not_supported' };
  if (!Number.isFinite(input.value) || input.value <= 0) {
    return { ok: false, reason: 'invalid_value' };
  }

  const ageDays = ageInDays(input.dateOfBirth, input.measuredOn);
  const lms = lmsForAge(input.measurementType, input.sex, ageDays);
  if (lms === null) {
    return REFERENCE_TABLE[input.measurementType] === null
      ? { ok: false, reason: 'no_reference_table' }
      : { ok: false, reason: 'age_out_of_range' };
  }

  const zScore = zScoreFromLms(input.value, lms);
  return {
    ok: true,
    ageDays,
    source: growthChartSourceForAgeDays(ageDays),
    zScore,
    percentile: percentileFromZScore(zScore),
  };
}
